// ────────────────────────────────────────────────────────────────────
// PlantTriagePanel — the plot-pane fallback when a plant has no spatial view
// (no plot plan, no PID, nothing positioned). Instead of a dead "no plot plan"
// notice, the primary pane answers the customer's actual first question:
// "what needs my attention in this plant?"
//
// Hero = a VERDICT, not a grid of equal tiles (2026-07-02 UX review round 2):
// the governing signal becomes one large statement with the worst offender
// named, backed by a distribution strip and compact facts. The table encodes
// each margin as a bullet bar against t-min — scannable without reading the
// numbers, but the numbers stay (credibility over decoration: every pixel of
// ink is a real value).
// ────────────────────────────────────────────────────────────────────

import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { apiClient, getCachedUser, STAFF_ROLES } from "@/lib/api";
import { tint } from "@/lib/utils";
import type { PlantTriage, TriageRiskRow, TriageRunwayRow } from "@/lib/types";

const CRIT = "var(--err)";
const WARN = "var(--warn)";
const OK = "var(--ok)";
const NONE = "#6E7585";


// Risk level → colour + short label. Colour is never the only channel: the
// row prints the level text and the ▲/– glyph beside it.
//
// These stay LITERAL on purpose, exactly like AssetWorkspaceScreen's
// RISK_COLOR. This is an ordinal magnitude ramp — high → medium-high → medium
// → medium-low → low, with #E0A500 interpolated between the amber and the
// green — not the crit/warn/ok status vocabulary above. Pointing four of its
// five bands at theme tokens would leave the fifth behind and stop the ramp
// reading monotonically the moment the palette moves. Here the colour IS the
// value, and it also feeds the hex-alpha suffixes the matrix cells are shaded
// with.
const RISK_META: Record<string, { color: string; label: string; glyph: string }> = {
  "high":        { color: "#C0392B", label: "High",     glyph: "▲" },
  "medium-high": { color: "#D97706", label: "Med-High", glyph: "▲" },
  "medium":      { color: "#E0A500", label: "Medium",   glyph: "–" },
  "medium-low":  { color: "#15803D", label: "Med-Low",  glyph: "–" },
  "low":         { color: "#15803D", label: "Low",      glyph: "–" },
};

function riskMeta(level: string): { color: string; label: string; glyph: string } {
  return RISK_META[level] ?? { color: NONE, label: level || "—", glyph: "–" };
}

// Mirror of server/agents/rbi/risk_constants.py MATRIX — keep in sync, or the
// highlighted cell will disagree with the risk label the engine printed.
const COF_LABELS = ["A", "B", "C", "D", "E"] as const;
const RISK_MATRIX: string[][] = [
  // CoF:  A            B              C              D              E
  ["low",         "low",          "medium-low",  "medium",      "medium"],       // PoF 1
  ["low",         "medium-low",   "medium",      "medium",      "medium-high"],  // PoF 2
  ["medium-low",  "medium",       "medium",      "medium-high", "high"],         // PoF 3
  ["medium",      "medium",       "medium-high", "high",        "high"],         // PoF 4
  ["medium",      "medium-high",  "high",        "high",        "high"],         // PoF 5
];

/** The API 581 5x5 in miniature: zones as faint fills, the asset's cell solid
 *  with an ink ring. Supplementary to the printed PoF/CoF text (never the only
 *  channel), so the tiny size costs nothing in accessibility. */
function RiskMatrixMini({ pof, cof }: { pof: number | null; cof: string | null }): JSX.Element | null {
  const ci = cof === null ? -1 : COF_LABELS.indexOf(cof as typeof COF_LABELS[number]);
  if (pof === null || pof < 1 || pof > 5 || ci < 0) return null;
  const CELL = 15;
  return (
    <div className="mt-2 font-mono select-none" style={{ fontSize: 7.5, color: "var(--ink-4)" }}>
      <div className="flex items-center gap-1.5">
        <span style={{ writingMode: "vertical-rl", transform: "rotate(180deg)", letterSpacing: ".14em" }}>POF</span>
        <div>
          {[5, 4, 3, 2, 1].map((p) => (
            <div key={p} className="flex items-center" style={{ gap: 2, marginBottom: 2 }}>
              <span className="tabular-nums text-right" style={{ width: 8, color: p === pof ? "var(--ink-1)" : "var(--ink-4)", fontWeight: p === pof ? 700 : 400 }}>{p}</span>
              {COF_LABELS.map((c, j) => {
                const zone = riskMeta(RISK_MATRIX[p - 1][j]).color;
                const here = p === pof && j === ci;
                return (
                  <span key={c} style={{
                    width: CELL, height: CELL, display: "inline-block", borderRadius: 2,
                    background: here ? zone : `${zone}2E`,
                    boxShadow: here ? "inset 0 0 0 1.5px var(--ink-1)" : `inset 0 0 0 1px ${zone}55`,
                  }} />
                );
              })}
            </div>
          ))}
          <div className="flex" style={{ gap: 2, paddingLeft: 10 }}>
            {COF_LABELS.map((c) => (
              <span key={c} className="text-center" style={{ width: CELL, color: c === cof ? "var(--ink-1)" : "var(--ink-4)", fontWeight: c === cof ? 700 : 400 }}>{c}</span>
            ))}
            <span style={{ letterSpacing: ".14em", marginLeft: 3 }}>COF</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── the reinspection runway ──────────────────────────────────────────
/** Each governing location plotted at its REMAINING LIFE on a shared time
 *  axis — a form the sidebar's status list cannot be confused with. Left is
 *  fire, right is comfort. Breaches pin in the OVERDUE block; single-survey
 *  locations (no rate) sit in an explicit "baseline needed" strip. */
function RunwayChart({ rows, okCount, dmRisk, riskSource, riskVintage, onPick }: {
  rows: TriageRunwayRow[]; okCount: number;
  dmRisk: TriageRiskRow[]; riskSource: string; riskVintage: string;
  onPick: (assetId: number) => void;
}): JSX.Element | null {
  if (rows.length === 0 && dmRisk.length === 0) {
    return (
      <div className="px-4 py-3 font-mono" style={{ fontSize: 10.5, color: "var(--ink-3)" }}>
        no problem locations — {okCount.toLocaleString()} measured locations are clear of t-min with ≥ 10 yr remaining life
      </div>
    );
  }
  const overdue = rows.filter((r) => r.rl_yr === 0);
  const unknown = rows.filter((r) => r.rl_yr === null);
  // Near t-min but losing wall slowly (>10 y of life): still a problem the
  // wall paints amber, but position on a 0-10y axis carries no information —
  // they live in their own strip instead of stacking at the right edge.
  const nearSlow = rows.filter((r) => r.rl_yr !== null && (r.rl_yr as number) >= 10);
  const plotted = rows.filter((r) => r.rl_yr !== null && r.rl_yr !== 0 && (r.rl_yr as number) < 10)
    .map((r) => ({ r, rlv: r.rl_yr as number }))
    .sort((a, b) => a.rlv - b.rlv);

  // A PAN/ZOOMABLE remaining-life axis (drag to pan · ctrl+wheel to zoom at
  // the cursor · buttons; double-click resets). Breached locations carry no
  // remaining life to plot — they live in the BELOW T-MIN strip instead
  // (user direction 2026-07-03).
  const NEG = 0;
  const [win, setWin] = useState<{ t0: number; t1: number }>({ t0: 0, t1: 10 });
  // Click = inspect (a detail popover); navigation is an explicit button in it.
  const [sel, setSel] = useState<{ r?: TriageRunwayRow; k?: TriageRiskRow; x: number; y: number } | null>(null);
  // Search: matching chips stay full strength, the rest fade back.
  const [q, setQ] = useState("");
  const matches = (r: TriageRunwayRow): boolean =>
    q.trim() === "" || `${r.tag} ${r.cml_no}`.toLowerCase().includes(q.trim().toLowerCase());
  const matchCount = q.trim() === "" ? null : rows.filter(matches).length;
  useEffect(() => {
    if (!sel) return;
    const close = (e: Event) => {
      if ((e.target as HTMLElement).closest?.(".runway-popover")) return;
      setSel(null);
    };
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") setSel(null); };
    document.addEventListener("pointerdown", close);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("pointerdown", close);
      document.removeEventListener("keydown", esc);
    };
  }, [sel]);
  const drag = useRef<{ px: number; t0: number; t1: number } | null>(null);
  const axisRef = useRef<HTMLDivElement | null>(null);
  const clampWin = (t0: number, t1: number): { t0: number; t1: number } => {
    let span = Math.min(Math.max(t1 - t0, 0.5), 12);
    let s = Math.max(Math.min(t0, 12 - span), 0);
    return { t0: s, t1: s + span };
  };
  const span = win.t1 - win.t0;
  const x = (rl: number) => NEG + ((rl - win.t0) / span) * (100 - NEG);
  // "nice" tick step for the current zoom
  const tickStep = span > 6 ? 2 : span > 3 ? 1 : 0.5;
  const ticks: number[] = [];
  for (let t = Math.ceil(win.t0 / tickStep) * tickStep; t <= win.t1 + 1e-9; t += tickStep) {
    ticks.push(Number(t.toFixed(2)));
  }
  const visible = plotted.filter((it) => it.rlv >= win.t0 && it.rlv <= win.t1);
  const hiddenLeft = plotted.filter((it) => it.rlv < win.t0).length;
  const hiddenRight = plotted.filter((it) => it.rlv > win.t1).length;
  // Greedy lane packing on the chips' REAL horizontal intervals: a chip is
  // ~14% of the axis wide and edge chips anchor left/right instead of centre
  // (so they never spill outside the canvas) — the interval accounts for it.
  const CHIP_W = 14;
  const anchorOf = (px: number): "left" | "right" | "center" =>
    px < 7 ? "left" : px > 93 ? "right" : "center";
  const ivOf = (px: number): [number, number] => {
    const a = anchorOf(px);
    return a === "left" ? [px, px + CHIP_W]
         : a === "right" ? [px - CHIP_W, px]
         : [px - CHIP_W / 2, px + CHIP_W / 2];
  };
  // PERMANENT lane per chip, assigned ONCE by packing every chip on the fully
  // zoomed-out domain (the most crowded mapping; any window only spreads chips
  // apart). Pan/zoom therefore never reshuffles rows or changes the height —
  // chips move horizontally only.
  const laneOf = useMemo(() => {
    const ends: number[] = [];
    const out = new Map<string, number>();
    const put = (key: string, px: number) => {
      const [start, end] = ivOf(px);
      let lane = ends.findIndex((e) => start - e >= 1.5);
      if (lane === -1) { lane = ends.length; ends.push(end); }
      else ends[lane] = end;
      out.set(key, lane);
    };
    plotted.forEach((it) =>
      put(`${it.r.asset_id}|${it.r.cml_no}`, NEG + (it.rlv / 12) * (100 - NEG)));
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);
  const placed = visible.map((it) => ({
    ...it, px: x(it.rlv),
    lane: laneOf.get(`${it.r.asset_id}|${it.r.cml_no}`) ?? 0,
  }));
  const lanes = Math.max(...Array.from(laneOf.values()), 0) + 1;
  const LANE_H = 30;

  const zoom = (factor: number, centerT?: number) => {
    const c = centerT ?? (win.t0 + win.t1) / 2;
    setWin(clampWin(c - (c - win.t0) * factor, c + (win.t1 - c) * factor));
  };
  const onPointerDown = (e: React.PointerEvent) => {
    if ((e.target as HTMLElement).closest("button")) return;   // chips stay clickable
    drag.current = { px: e.clientX, t0: win.t0, t1: win.t1 };
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (!drag.current || !axisRef.current) return;
    const w = axisRef.current.clientWidth * (1 - NEG / 100);
    const dt = ((drag.current.px - e.clientX) / Math.max(w, 1)) * (drag.current.t1 - drag.current.t0);
    setWin(clampWin(drag.current.t0 + dt, drag.current.t1 + dt));
  };
  const onPointerUp = () => { drag.current = null; };
  const onWheel = (e: React.WheelEvent) => {
    if (!e.ctrlKey && !e.metaKey) return;              // plain wheel scrolls the page
    e.preventDefault();
    let centerT: number | undefined;
    if (axisRef.current) {
      const rect = axisRef.current.getBoundingClientRect();
      const fx = ((e.clientX - rect.left) / rect.width) * 100;
      if (fx > NEG) centerT = win.t0 + ((fx - NEG) / (100 - NEG)) * span;
    }
    zoom(e.deltaY > 0 ? 1.25 : 0.8, centerT);
  };
  const short = (tag: string) => tag.split(/[\s]/).pop() ?? tag;

  const chip = (r: TriageRunwayRow, color: string, bg: string, suffix?: string) => (
    <button key={`${r.asset_id}-${r.cml_no}`}
            onClick={(e) => { e.stopPropagation(); setSel({ r, x: e.clientX, y: e.clientY }); }}
            className="font-mono px-1.5 py-0.5 cursor-pointer whitespace-nowrap"
            style={{ fontSize: 10, fontWeight: 700, color, background: bg,
                     border: `1px solid ${tint(color, 33)}`, borderRadius: 3, lineHeight: 1.4,
                     opacity: matches(r) ? 1 : 0.18,
                     outline: sel?.r === r ? `2px solid ${color}` : undefined }}>
      {short(r.tag)}
      <span style={{ fontWeight: 400, opacity: 0.75 }}> · {r.cml_no}</span>
      {suffix && <span style={{ fontWeight: 700, marginLeft: 5 }}>{suffix}</span>}
    </button>
  );

  // What this location is asking for — same rules as everywhere else.
  const actionOf = (r: TriageRunwayRow): { text: string; color: string } =>
    r.rl_yr === 0 ? { text: "evaluate now · FFS / renew", color: CRIT }
    : r.rl_yr === null ? { text: "baseline survey needed", color: WARN }
    : r.rl_yr < 4 ? { text: `resurvey ≤ ${Math.max(r.rl_yr / 2, 0.5).toFixed(1)} yr`, color: WARN }
    : { text: "monitor", color: "var(--ink-3)" };

  return (
    <div className="flex items-stretch">
      {/* one continuous canvas: breach-depth zone (mm) left of 0, time right.
          Drag pans · ctrl+wheel zooms at the cursor · double-click resets. */}
      <div className="flex-1 min-w-0 px-4 py-3">
        <div className="flex items-center justify-end gap-1.5 mb-1">
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="find tag / CML…"
                 className="font-mono px-2 mr-auto border"
                 style={{ fontSize: 10, height: 20, width: 150, borderColor: "var(--hairline)",
                          background: "var(--paper)", color: "var(--ink-1)", borderRadius: 3 }} />
          {matchCount !== null && (
            <span className="font-mono mr-2" style={{ fontSize: 9, color: matchCount > 0 ? "var(--accent-deep)" : CRIT, fontWeight: 700 }}>
              {matchCount} match{matchCount === 1 ? "" : "es"}
            </span>
          )}
          <span className="font-mono hidden md:inline" style={{ fontSize: 9, color: "var(--ink-4)" }}>
            <span className="inline-block w-[8px] h-[8px] align-[-1px]" style={{ background: tint(CRIT, 9), border: `1px solid ${tint(CRIT, 27)}` }} /> act ≤2y
            <span className="inline-block w-[8px] h-[8px] align-[-1px] ml-1.5" style={{ background: tint(WARN, 9), border: `1px solid ${tint(WARN, 27)}` }} /> plan 2–8y
            <span className="ml-2">drag = pan · ctrl+scroll = zoom</span>
          </span>
          {hiddenLeft > 0 && (
            <span className="font-mono mr-auto" style={{ fontSize: 9, color: WARN, fontWeight: 700 }}>← {hiddenLeft} earlier</span>
          )}
          {hiddenRight > 0 && (
            <span className="font-mono" style={{ fontSize: 9, color: "var(--ink-4)" }}>{hiddenRight} later →</span>
          )}
          <span className="font-mono" style={{ fontSize: 9, color: "var(--ink-4)" }}>
            {win.t0.toFixed(1)}–{win.t1.toFixed(1)} yr
          </span>
          {([["−", 1.25], ["+", 0.8]] as [string, number][]).map(([lbl, f]) => (
            <button key={lbl} onClick={() => zoom(f)} title={lbl === "+" ? "Zoom in" : "Zoom out"}
                    className="font-mono px-1.5 leading-none border hover:text-[var(--accent)]"
                    style={{ fontSize: 11, height: 18, borderColor: "var(--hairline)", color: "var(--ink-3)", background: "var(--paper)" }}>
              {lbl}
            </button>
          ))}
          <button onClick={() => setWin({ t0: 0, t1: 10 })} title="Reset view"
                  className="font-mono px-1.5 leading-none border hover:text-[var(--accent)]"
                  style={{ fontSize: 10, height: 18, borderColor: "var(--hairline)", color: "var(--ink-3)", background: "var(--paper)" }}>
            ⟲
          </button>
        </div>
        <div ref={axisRef} className="relative select-none overflow-hidden"
             onPointerDown={onPointerDown} onPointerMove={onPointerMove}
             onPointerUp={onPointerUp} onPointerCancel={onPointerUp}
             onWheel={onWheel} onDoubleClick={() => setWin({ t0: 0, t1: 10 })}
             style={{ height: lanes * LANE_H + 40, cursor: drag.current ? "grabbing" : "grab",
                      touchAction: "pan-y" }}>
          {/* zone tints: act (<2y), plan (2-8y) — boundaries from interval = RL/2 */}
          {(() => {
            const clip = (v: number) => Math.max(Math.min(v, 100), NEG);
            const act0 = clip(x(0)), act1 = clip(x(2)), plan1 = clip(x(8));
            return (
              <>
                {act1 > act0 && <div className="absolute inset-y-0" style={{ left: `${act0}%`, width: `${act1 - act0}%`, background: tint(CRIT, 3) }} />}
                {plan1 > act1 && <div className="absolute inset-y-0" style={{ left: `${act1}%`, width: `${plan1 - act1}%`, background: tint(WARN, 3) }} />}
              </>
            );
          })()}
          {ticks.map((yr) => (
            <div key={yr} className="absolute inset-y-0" style={{ left: `${x(yr)}%` }}>
              <div className="absolute inset-y-0" style={{ width: 1, background: "var(--hairline)" }} />
              <span className="absolute bottom-0 font-mono" style={{ fontSize: 8.5, color: "var(--ink-4)", transform: "translateX(-50%)" }}>
                {yr === 10 ? "10+" : tickStep < 1 ? yr.toFixed(1) : yr}
              </span>
            </div>
          ))}
          {placed.map((it) => (
            <div key={`${it.r.asset_id}-${it.r.cml_no}`} className="absolute"
                 style={{ left: `${Math.min(it.px, 100)}%`, top: it.lane * LANE_H + 14,
                          transform: `translateX(${anchorOf(it.px) === "left" ? "0%" : anchorOf(it.px) === "right" ? "-100%" : "-50%"})` }}>
              {chip(it.r, it.rlv < 2 ? CRIT : it.rlv < 8 ? WARN : OK,
                    it.rlv < 2 ? tint(CRIT, 8) : it.rlv < 8 ? tint(WARN, 8) : tint(OK, 6))}
              <div className="font-mono text-center whitespace-nowrap" style={{ fontSize: 8.5, color: "var(--ink-4)" }}>
                {(it.r.rl_yr as number) > 10 ? ">10" : (it.r.rl_yr as number).toFixed(1)}y
              </div>
            </div>
          ))}
        </div>
        {overdue.length > 0 && (
          <div className="flex items-center gap-1.5 flex-wrap mt-2 pt-2 border-t" style={{ borderColor: "var(--hairline)" }}>
            <span className="font-mono uppercase" style={{ fontSize: 8.5, letterSpacing: ".14em", color: CRIT, fontWeight: 700 }}>
              ▼ below t-min · {overdue.length}
            </span>
            {overdue.slice().sort((a, b) => a.margin_mm - b.margin_mm)
              .map((r) => chip(r, CRIT, tint(CRIT, 8), `${r.margin_mm.toFixed(2)} mm`))}
            <span className="font-mono" style={{ fontSize: 9, color: "var(--ink-4)" }}>evaluate now — FFS / renew</span>
          </div>
        )}
        {nearSlow.length > 0 && (
          <div className="flex items-center gap-1.5 flex-wrap mt-2 pt-2 border-t" style={{ borderColor: "var(--hairline)" }}>
            <span className="font-mono uppercase" style={{ fontSize: 8.5, letterSpacing: ".14em", color: WARN, fontWeight: 700 }}>
              near t-min · slow loss · {nearSlow.length}
            </span>
            {nearSlow.map((r) => chip(r, WARN, tint(WARN, 6)))}
            <span className="font-mono" style={{ fontSize: 9, color: "var(--ink-4)" }}>within 20% of t-min · &gt;10 yr at current rate</span>
          </div>
        )}
        {unknown.length > 0 && (
          <div className="flex items-center gap-1.5 flex-wrap mt-2 pt-2 border-t" style={{ borderColor: "var(--hairline)" }}>
            <span className="font-mono uppercase" style={{ fontSize: 8.5, letterSpacing: ".14em", color: WARN, fontWeight: 700 }}>
              baseline needed · {unknown.length}
            </span>
            {unknown.map((r) => chip(r, WARN, tint(WARN, 6)))}
            <span className="font-mono" style={{ fontSize: 9, color: "var(--ink-4)" }}>near t-min · single survey — no rate yet</span>
          </div>
        )}
        {dmRisk.length > 0 && (
          <div className="flex items-center gap-1.5 flex-wrap mt-2 pt-2 border-t" style={{ borderColor: "var(--hairline)" }}>
            <span className="font-mono uppercase" style={{ fontSize: 8.5, letterSpacing: ".14em", color: CRIT, fontWeight: 700 }}>
              ▲ dm-driven risk · {dmRisk.length}
            </span>
            {dmRisk.map((k) => (
              <button key={k.asset_id}
                      onClick={(e) => { e.stopPropagation(); setSel({ k, x: e.clientX, y: e.clientY }); }}
                      className="font-mono px-1.5 py-0.5 cursor-pointer whitespace-nowrap"
                      style={{ fontSize: 10, fontWeight: 700, color: riskMeta(k.risk_level).color,
                               background: `${riskMeta(k.risk_level).color}14`,
                               border: `1px solid ${riskMeta(k.risk_level).color}55`, borderRadius: 3, lineHeight: 1.4,
                               opacity: q.trim() === "" || k.tag.toLowerCase().includes(q.trim().toLowerCase()) ? 1 : 0.18 }}>
                {short(k.tag)}
                <span style={{ fontWeight: 700, marginLeft: 5 }}>{riskMeta(k.risk_level).glyph} {riskMeta(k.risk_level).label}</span>
              </button>
            ))}
            <span className="font-mono" style={{ fontSize: 9, color: "var(--ink-4)" }}>
              high risk NOT driven by wall loss (SCC / CUI / consequence) · source: {riskSource === "study" ? (riskVintage || "imported study") : "engine — preliminary"}
            </span>
          </div>
        )}
        <div className="font-mono mt-2 pt-1.5 border-t" style={{ fontSize: 9, color: "var(--ink-4)", borderColor: "var(--hairline)" }}>
          showing all {rows.length.toLocaleString()} problem locations · {okCount.toLocaleString()} healthy locations screened out (rem. life ≥ 10 yr, clear of t-min)
        </div>
      </div>
      {sel && (() => {
        const left = Math.min(sel.x + 12, window.innerWidth - 280);
        // Estimated card heights (matrix adds ~95px) keep the popover on-screen.
        const est = sel.k ? 320 : sel.r?.risk_level ? 340 : 240;
        const top = Math.min(sel.y + 10, window.innerHeight - est);
        if (sel.k) {
          const m = riskMeta(sel.k.risk_level);
          return (
            <div className="runway-popover fixed z-[1500] border shadow-soft px-3.5 py-3"
                 style={{ left, top, width: 264, background: "var(--paper)",
                          borderColor: "var(--hairline)", borderRadius: 6 }}>
              <div className="font-mono" style={{ fontSize: 12, fontWeight: 700, color: "var(--accent-deep)" }}>{sel.k.tag}</div>
              <div className="font-mono mt-1.5" style={{ fontSize: 11, fontWeight: 700, color: m.color }}>
                {m.glyph} {m.label} risk
                <span style={{ color: "var(--ink-3)", fontWeight: 400 }}> · PoF {sel.k.pof_category ?? "—"} / CoF {sel.k.cof_category ?? "—"}</span>
              </div>
              <RiskMatrixMini pof={sel.k.pof_category} cof={sel.k.cof_category} />
              <div className="font-mono mt-1.5" style={{ fontSize: 10, color: "var(--ink-3)", lineHeight: 1.5 }}>
                {sel.k.measured_cmls > 0
                  ? <>wall is healthy at all {sel.k.measured_cmls} measured locations — risk is damage-mechanism / consequence driven (SCC, CUI). Wall loss alone will not warn here.</>
                  : <>no assessable wall data (readings or t-min missing) — risk is damage-mechanism / consequence driven (SCC, CUI). Establish t-min + baseline coverage.</>}
              </div>
              <button onClick={() => onPick(sel.k!.asset_id)}
                      className="font-mono w-full mt-2.5 py-1 border transition-colors hover:bg-[rgba(var(--accent-rgb)_/_0.08)]"
                      style={{ fontSize: 10, fontWeight: 700, letterSpacing: ".08em",
                               borderColor: "var(--accent)", color: "var(--accent-deep)",
                               background: "transparent", borderRadius: 4 }}>
                OPEN ASSET →
              </button>
            </div>
          );
        }
        if (!sel.r) return null;
        const rr = sel.r;
        const a = actionOf(rr);
        return (
          <div className="runway-popover fixed z-[1500] border shadow-soft px-3.5 py-3"
               style={{ left, top, width: 264, background: "var(--paper)",
                        borderColor: "var(--hairline)", borderRadius: 6 }}>
            <div className="font-mono" style={{ fontSize: 12, fontWeight: 700, color: "var(--accent-deep)" }}>
              {rr.tag}
              <span style={{ color: "var(--ink-3)", fontWeight: 400 }}> · CML {rr.cml_no}</span>
            </div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 mt-2 font-mono" style={{ fontSize: 10.5 }}>
              <span style={{ color: "var(--ink-3)" }}>margin</span>
              <span className="text-right tabular-nums font-bold"
                    style={{ color: rr.margin_mm <= 0 ? CRIT : "var(--ink-1)" }}>
                {rr.margin_mm >= 0 ? "+" : ""}{rr.margin_mm.toFixed(2)} mm
              </span>
              <span style={{ color: "var(--ink-3)" }}>LT rate</span>
              <span className="text-right tabular-nums" style={{ color: "var(--ink-2)" }}>
                {rr.cr_mm_yr !== null ? `${rr.cr_mm_yr.toFixed(3)} mm/yr` : "—"}
              </span>
              <span style={{ color: "var(--ink-3)" }}>rem. life</span>
              <span className="text-right tabular-nums font-bold"
                    style={{ color: rr.rl_yr === 0 ? CRIT : (rr.rl_yr ?? 99) < 4 ? WARN : OK }}>
                {rr.rl_yr === null ? "—" : rr.rl_yr === 0 ? "0" : rr.rl_yr > 10 ? ">10" : rr.rl_yr.toFixed(1)} yr
              </span>
              <span style={{ color: "var(--ink-3)" }}>surveys</span>
              <span className="text-right tabular-nums" style={{ color: "var(--ink-2)" }}>{rr.surveys}</span>
            </div>
            {rr.risk_level && (
              <div className="font-mono mt-1.5" style={{ fontSize: 10 }}>
                <span style={{ color: "var(--ink-3)" }}>RBI risk </span>
                <span style={{ color: riskMeta(rr.risk_level).color, fontWeight: 700 }}>
                  {riskMeta(rr.risk_level).glyph} {riskMeta(rr.risk_level).label}
                </span>
                <span style={{ color: "var(--ink-3)" }}> · PoF {rr.pof_category ?? "—"} / CoF {rr.cof_category ?? "—"}</span>
                <RiskMatrixMini pof={rr.pof_category} cof={rr.cof_category} />
              </div>
            )}
            <div className="font-mono mt-2 pt-2 border-t" style={{ fontSize: 10.5, fontWeight: 700, color: a.color, borderColor: "var(--hairline)" }}>
              {a.text}
            </div>
            <button onClick={() => onPick(rr.asset_id)}
                    className="font-mono w-full mt-2.5 py-1 border transition-colors hover:bg-[rgba(var(--accent-rgb)_/_0.08)]"
                    style={{ fontSize: 10, fontWeight: 700, letterSpacing: ".08em",
                             borderColor: "var(--accent)", color: "var(--accent-deep)",
                             background: "transparent", borderRadius: 4 }}>
              OPEN ASSET →
            </button>
          </div>
        );
      })()}
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <div className="font-mono uppercase px-4 py-2 border-b" style={{ fontSize: 10, letterSpacing: ".18em", color: "var(--ink-3)", borderColor: "var(--hairline)" }}>
      {children}
    </div>
  );
}


// ── panel ────────────────────────────────────────────────────────────
export function PlantTriagePanel({ plantId, assetCount, showSetupHint = true }: {
  plantId: number; assetCount: number;
  /** false when shown as a TAB on a plant that already has spatial views. */
  showSetupHint?: boolean;
}): JSX.Element {
  const navigate = useNavigate();
  const q = useQuery({
    queryKey: ["plant-triage", plantId],
    queryFn: () => apiClient.plantTriage(plantId),
    enabled: !!plantId,
    staleTime: 60_000,
  });
  const isStaff = (STAFF_ROLES as readonly string[]).includes(getCachedUser()?.role ?? "");

  const t: PlantTriage | undefined = q.data;

  if (q.isLoading) {
    return (
      <div className="absolute inset-0 flex items-center justify-center font-mono text-xs" style={{ color: "var(--ink-3)" }}>
        Loading integrity triage…
      </div>
    );
  }
  if (!t) {
    return (
      <div className="absolute inset-0 flex items-center justify-center font-mono text-xs" style={{ color: "var(--ink-3)" }}>
        Triage unavailable
      </div>
    );
  }

  const hasRisk = t.risk_rows.length > 0;
  const hasThickness = t.measured_cmls > 0;

  return (
    <div className="absolute inset-0 overflow-y-auto" style={{ background: "var(--paper-2)" }}>
      {/* full-bleed: the wall gains columns (shorter field) and the runway
          gains lane room — empty side margins serve nothing here */}
      <div className="w-full px-6 py-5 flex flex-col gap-4">


        {/* thickness — governing CML per asset, margin as a bullet bar vs t-min */}
        {hasThickness && (
          <div className="border" style={{ borderColor: "var(--hairline)", background: "var(--paper)" }}>
            <SectionLabel>
              / Reinspection runway · governing location per asset
              <span className="ml-2 normal-case" style={{ letterSpacing: 0, color: "var(--ink-3)" }}>
                rem. life = margin ÷ LT rate · preliminary, not engineer-verified
              </span>
            </SectionLabel>
            <RunwayChart rows={t.runway_rows ?? []} okCount={t.runway_ok ?? 0}
                         dmRisk={t.dm_risk_rows ?? []}
                         riskSource={t.risk_source} riskVintage={t.risk_vintage}
                         onPick={(assetId) => navigate(`/assets/${assetId}`)} />
            
          </div>
        )}

        {!hasRisk && !hasThickness && (
          <div className="border px-4 py-6 text-center font-sans" style={{ borderColor: "var(--hairline)", fontSize: 13, color: "var(--ink-3)" }}>
            No integrity data on file yet for this plant’s {assetCount} assets.
          </div>
        )}

        {/* staff-only: the spatial views are an admin setup task, not a customer ask */}
        {isStaff && showSetupHint && (
          <div className="font-mono px-1" style={{ fontSize: 9.5, color: "var(--ink-4)" }}>
            staff · register a plot plan + position assets in Asset Studio to enable the spatial integrity map here
          </div>
        )}
      </div>
    </div>
  );
}
